import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import StateContext from '../context/State';
import PortalService from '../services/portal.service';

/**
 * Custom hook to fetch consent form configuration and calculate if it's required
 * @param enabled - Whether the query should run (typically based on modal visibility)
 * @returns Object with:
 *   - consentFormConfig: The full consent form configuration
 *   - isConsentFormRequired: Computed boolean indicating if any consent form is required
 *   - isLoading: Whether the query is currently fetching
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually refetch the data
 */
export function useConsentFormInformationQuery(enabled = true) {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    
    const query = useQuery({
        queryKey: ['consentFormInformation', state.portalId],
        queryFn: async () => {
            const consentFormInfo = await portalService.getClientPortalConsentFormInformation(state.portalId);
            
            const isRequired = 
                consentFormInfo.DefaultConsentRequired || 
                consentFormInfo.TextBoxConsentRequired || 
                consentFormInfo.UploadConsent;
            
            return {
                config: consentFormInfo,
                isRequired: isRequired
            };
        },
        enabled: enabled && Boolean(state.portalId), // Only fetch when enabled and we have a valid portalId
        staleTime: 0,          // Always consider stale (refetch when modal opens)
        refetchOnMount: true,  // Refetch each time the component mounts
    });

    return {
        consentFormConfig: query.data?.config,
        isConsentFormRequired: query.data?.isRequired ?? false,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch
    };
}

